import { DropdownOption, Questionnaire, QuestionnaireData } from "@teamprevent/common/questionnaires/model";

import { OhCaseDetailModel } from "../crud/ohCase/model";
import { PpqOutcomeId } from "../triage/models";

export interface HealthSurveillanceCaseCreateModel {
    healthSurveillanceCaseTypes: HealthSurveillanceCaseType[];
}

export interface LinkedHealthSurveillanceCaseCreateModel extends HealthSurveillanceCaseCreateModel {
    linkedCaseId: string;
    createReviewPpqDeclarationTask: boolean;
    workGroupIds: string[];
    ppqOutcome: PpqOutcomeId;
}

export interface ReopenHealthSurveillanceCaseModel {
    reason: string;
}

export interface ClinicHealthSurveillanceQuestionnaire {
    clinicId: string;
    appointmentId: string;
    questionnaire: Questionnaire | null;
}

export interface HealthSurveillanceState {
    questionnaires: ClinicHealthSurveillanceQuestionnaire[];
}

export type HealthSurveillanceCaseType = "HealthSurveillance" | "FitForWork";

export const caseTypeOptions: { text: string; value: string; values: HealthSurveillanceCaseType[] }[] = [
    { text: "Health Surveillance", value: "HealthSurveillance", values: ["HealthSurveillance"] },
    { text: "Fit for Work", value: "FitForWork", values: ["FitForWork"] },
    { text: "Health Surveillance & Fit for Work", value: "Both", values: ["HealthSurveillance", "FitForWork"] }
];

export const HealthSurveillanceHandsImageUrl = "/assets/health-surveillance-hands.png";
export const HealthSurveillanceBodyImageUrl = "/assets/health-surveillance-body.png";

export enum AudiometryHearingCategory {
    Category1NormalHearing = "Category1NormalHearing",
    Category2EarlyWarning = "Category2EarlyWarning",
    Category3Referral = "Category3Referral",
    Category4RapidHearingLossReferral = "Category4RapidHearingLossReferral"
}

export const audiometryHearingCategoryOptions: { text: string; value: string }[] = [
    { text: "Category 1 (Normal Hearing)", value: AudiometryHearingCategory.Category1NormalHearing },
    { text: "Category 2 (Early Warning)", value: AudiometryHearingCategory.Category2EarlyWarning },
    { text: "Category 3 (Referral)", value: AudiometryHearingCategory.Category3Referral },
    { text: "Category 4 (Rapid Hearing Loss - Referral)", value: AudiometryHearingCategory.Category4RapidHearingLossReferral }
];

export interface HealthSurveillanceAutoSaveDto {
    id: string;
    data: QuestionnaireData;
    _etag: string;
}

export interface HealthSurveillanceQuestionnaireAutoSaveDto {
    id: string;
    data: QuestionnaireData;
    eTag: string;
}

export interface SubmitClinicHealthSurveillanceQuestionnaireResponseDto {
    id: string;
    ohCaseModel: OhCaseDetailModel;
}

export const PassOption = "Pass";
export const FailOption = "Fail";

export const PulseRhythmRegular = "Regular";
export const PulseRhythmIrregular = "Irregular";

export const eyeVisionPeripheralFieldOptions: DropdownOption[] = [
    { text: "70 degrees or greater: satisfactory", value: "70 degrees or greater: satisfactory" },
    {
        text: "Less than 70 degrees: refer to optician and advise employee of implications for driving",
        value: "Less than 70 degrees: refer to optician and advise employee of implications for driving"
    },
    { text: "Pass", value: "Pass" },
    { text: "Fail", value: "Fail" }
];

export const visionHorizontalFieldOptions: DropdownOption[] = [
    { text: "Normal", value: "Normal" },
    { text: "Left upper deficit", value: "Left upper deficit" },
    { text: "Right upper deficit", value: "Right upper deficit" },
    { text: "Left lower deficit", value: "Left lower deficit" },
    { text: "Right lower deficit", value: "Right lower deficit" },
    {
        text: "Refer to optician if any dizziness experienced at test or result other than normal - NB working at height?",
        value: "Refer to optician if any dizziness experienced at test or result other than normal - NB working at height?"
    },
    { text: "Pass", value: "Pass" },
    { text: "Fail", value: "Fail" }
];

export const distanceTestResultOptions: DropdownOption[] = [
    { text: "3/6 Satisfactory", value: "3/6 Satisfactory" },
    { text: "3/7.5 Satisfactory", value: "3/7.5 Satisfactory" },
    { text: "3/9 Unacceptable - refer to optician", value: "3/9 Unacceptable - refer to optician" },
    { text: "3/12 Unacceptable - refer to optician", value: "3/12 Unacceptable - refer to optician" },
    { text: "3/15 Unacceptable - refer to optician", value: "3/15 Unacceptable - refer to optician", old: true },
    { text: "3/18 Unacceptable - refer to optician", value: "3/18 Unacceptable - refer to optician" },
    { text: "3/24 Unacceptable - refer to optician", value: "3/24 Unacceptable - refer to optician" },
    { text: "3/34 Unacceptable - refer to optician", value: "3/34 Unacceptable - refer to optician", old: true },
    { text: "3/36 Unacceptable - refer to optician", value: "3/36 Unacceptable - refer to optician" },
    { text: "3/60 Unacceptable - refer to physician", value: "3/60 Unnaceptable - refer to physician" },
    { text: "6/6 Satisfactory", value: "6/6 Satisfactory" },
    { text: "6/7.5 Satisfactory", value: "6/7.5 Satisfactory" },
    { text: "6/9 Satisfactory", value: "6/9 Satisfactory", old: true },
    { text: "6/9 Unacceptable - refer to optician", value: "6/9 Unacceptable - refer to optician" },
    { text: "6/12 Marginal but acceptable - still refer to optician", value: "6/12 Marginal but acceptable - still refer to optician", old: true },
    { text: "6/12 Unacceptable - refer to optician", value: "6/12 Unacceptable - refer to optician" },
    { text: "6/15 Unacceptable - refer to optician", value: "6/15 Unacceptable - refer to optician", old: true },
    { text: "6/18 Unacceptable - refer to optician", value: "6/18 Unacceptable - refer to optician" },
    { text: "6/21 Unacceptable - refer to optician", value: "6/21 Unacceptable - refer to optician", old: true },
    { text: "6/24 Unacceptable - refer to optician", value: "6/24 Unacceptable - refer to optician" },
    { text: "6/30 Unacceptable - refer to optician", value: "6/30 Unacceptable - refer to optician", old: true },
    { text: "6/34 Unacceptable - refer to optician", value: "6/34 Unacceptable - refer to optician", old: true },
    { text: "6/36 Unacceptable - refer to optician", value: "6/36 Unacceptable - refer to optician" },
    { text: "6/60 Unacceptable - refer to optician", value: "6/60 Unnaceptable - refer to optician", old: true },
    { text: "6/60 Unacceptable - refer to physician", value: "6/60 Unnaceptable - refer to physician" },
];

export const newDistanceTestResultOptions = [
    "6/6 Satisfactory",
    "6/7.5 Satisfactory",
    "6/9 Satisfactory",
    "6/12 Marginal but acceptable - still refer to optician",
    "6/15 Unacceptable - refer to optician",
    "6/18 Unacceptable - refer to optician",
    "6/21 Unacceptable - refer to optician",
    "6/30 Unacceptable - refer to optician",
    "6/60 Unacceptable - refer to optician"
].map(o => {
    return {
        text: o,
        value: o,
        old: false
    } as DropdownOption;
});

export const intermediateNearTestResultOptions = [
    "N.4 Satisfactory", "N.5 Satisfactory", "N.6 Satisfactory", "N.8 Marginal but acceptable - refer to optician",
    "N.10 Unacceptable - refer to optician", "N.12 Unacceptable - refer to optician", "N.14 Unacceptable - refer to optician",
    "N.18 Unacceptable - refer to optician", "N.36 Unacceptable - refer to optician"
].map(o => ({ text: o, value: o }));
